import React from "react";
import dayjs from "dayjs";
import { Form } from "antd";
import Button from "@/common/elements/Button";
import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import {
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import useDialog from "@/hooks/useDialog";
import { getOptionsFromRefList } from "@/util/common_function";
import { useSelector } from "react-redux";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { SCHEMA_NAME } from "@/util/common_variable";

const JailIncidenHearingInformation = ({ data, isFieldEnable, startingPoint = "" }) => {
  const { showDialog } = useDialog();

  const jailIncidentsInvolvedInmateSanctions = Form.useWatch(
    "jailIncidentsInvolvedInmateSanctions",
    data,
  );
  console.log(
    "🚀 ~ JailIncidenHearingInformation ~ jailIncidentsInvolvedInmateSanctions:",
    jailIncidentsInvolvedInmateSanctions,
  );

  const onHandleViewComment = () => {
    showDialog("info", {
      content: data?.getFieldValue("hearingComments"),
      confirmButtonText: "Ok",
    });
  };

  return (
    <>
      <StyledSectionHeading marginTop="20px">
        <span>Hearing Information</span>
      </StyledSectionHeading>

      <FieldSecurityProvider
        entity={SCHEMA_NAME.JAIL_INCIDENTS_INVOLVED_INMATE}
        startingPoint={startingPoint}
        bookingsFieldsEnabled={isFieldEnable}
      >
        {/* Hearing Information Text */}
        <StyledGridRow gutter={16}>
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="pendingHearing"
              formItemProps={{ 
                label: "Hearing Information",
                name: "pendingHearing",
              }}
            >
              <Select
                options={getOptionsFromRefList("PendingHearing")}
                disabled={!isFieldEnable}
              />
            </SecureFormItem>
          </StyledGridCol>
        </StyledGridRow>

        <StyledGridRow gutter={16}>
          <StyledGridCol lg={7}>
            <SecureFormItem
              fieldName="startDateTime"
              formItemProps={{ 
                label: "Start Date/Time",
                name: "startDateTime",
                dependencies: ["pendingHearing"],
                rules: [
                  ({ getFieldValue }) => ({
                    required: !!getFieldValue("pendingHearing"),
                    message: "Start Date/Time is required",
                  }),
                ],
              }}
            >
              <CommonDatePicker
                format="MM/DD/YYYY HH:mm"
                disabled={!isFieldEnable}
              />
            </SecureFormItem>
          </StyledGridCol>
          <StyledGridCol lg={7}>
            <SecureFormItem
              fieldName="hearingDueDateTime"
              formItemProps={{ 
                label: "Hearing Due Date/Time",
                name: "hearingDueDateTime",
                dependencies: ["pendingHearing"],
                rules: [
                  ({ getFieldValue }) => ({
                    required: !!getFieldValue("pendingHearing"),
                    message: "Hearing Due Date/Time is required",
                  }),
                ],
              }}
            >
              <CommonDatePicker
                format="MM/DD/YYYY HH:mm"
                disabled={!isFieldEnable}
              />
            </SecureFormItem>
          </StyledGridCol>
        </StyledGridRow>

        <StyledGridRow gutter={16}>
          <StyledGridCol lg={7}>
            <SecureFormItem
              fieldName="hearingDateTime"
              formItemProps={{ 
                label: "Hearing Date/Time",
                name: "hearingDateTime",
              }}
            >
              <CommonDatePicker
                format="MM/DD/YYYY HH:mm"
                disabled={!isFieldEnable}
              />
            </SecureFormItem>
          </StyledGridCol>
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="hearingOfficerStaffID"
              formItemProps={{ 
                label: "Hearing Officer",
                name: "hearingOfficerStaffID",
              }}
            >
              <Select
                options={getOptionsFromRefList("", true, false, true)}
                disabled={!isFieldEnable}
              />
            </SecureFormItem>
          </StyledGridCol>
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="reviewOfficerStaffID"
              formItemProps={{ 
                label: "Review Officer",
                name: "reviewOfficerStaffID",
              }}
            >
              <Select
                options={getOptionsFromRefList("", true, false, true)}
                disabled={!isFieldEnable}
              />
            </SecureFormItem>
          </StyledGridCol>
          <StyledGridCol lg={5}>
            <SecureFormItem
              fieldName="jailIncidentsResults"
              formItemProps={{ 
                label: "Results",
                name: "jailIncidentsResults",
              }}
            >
              <Select
                options={getOptionsFromRefList("JailIncidentsResults")}
                disabled={!isFieldEnable}
              />
            </SecureFormItem>
          </StyledGridCol>
          <StyledGridCol xs={24} lg={24}>
            <SecureFormItem
              fieldName="hearingComments"
              formItemProps={{ 
                label: "Comments",
                name: "hearingComments",
              }}
            >
              <TextField
                inputType="textarea"
                rows="3"
                disabled={!isFieldEnable}
              />
            </SecureFormItem>
            <Button type="primary" color="primary" onClick={onHandleViewComment}>
              View Comments
            </Button>
          </StyledGridCol>
        </StyledGridRow>

        {/* Penalty Section */}
        <StyledGridRow gutter={16}>
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="penaltyLength"
              formItemProps={{ 
                label: "Penalty Length",
                name: "penaltyLength",
                rules: jailIncidentsInvolvedInmateSanctions?.length > 0
                  ? [
                      {
                        required: true,
                      },
                    ]
                  : [],
              }}
            >
              <TextField disabled={!isFieldEnable} />
            </SecureFormItem>
          </StyledGridCol>
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="penaltyStartDateTime"
              formItemProps={{ 
                label: "Start Date/Time",
                name: "penaltyStartDateTime",
                rules: jailIncidentsInvolvedInmateSanctions?.length > 0
                  ? [
                      {
                        required: true,
                      },
                    ]
                  : [],
              }}
            >
              <CommonDatePicker
                format="MM/DD/YYYY HH:mm"
                disabled={!isFieldEnable}
              />
            </SecureFormItem>
          </StyledGridCol>
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="penaltyEndDateTime"
              formItemProps={{ 
                label: "End Date/Time",
                name: "penaltyEndDateTime",
                rules: jailIncidentsInvolvedInmateSanctions?.length > 0
                  ? [
                      {
                        required: true,
                      },
                    ]
                  : [],
              }}
            >
              <CommonDatePicker
                format="MM/DD/YYYY HH:mm"
                disabled={!isFieldEnable}
              />
            </SecureFormItem>
          </StyledGridCol>
        </StyledGridRow>
      </FieldSecurityProvider>
    </>
  );
};

export default JailIncidenHearingInformation;
